import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ExcelService, ExcelSheet } from 'src/app/shared/excel/excel-style.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { keyBy } from 'lodash';
import { BatteryMetricItem, LegendGroup } from '../utilization-reports/widgets/utilization-metric-box/utilization-metrics.interface';
import { SiteDashboardService } from '../../../site-dashboard.service';

@Component({
	selector: 'app-truck-exception-reports',
	templateUrl: './truck-exception-reports.component.html',
	styleUrls: ['./truck-exception-reports.component.css']
})
export class TruckExceptionReportsComponent implements OnInit, OnDestroy{

	siteSub: Subscription = new Subscription();
	siteInfo: any;
	devices: any[] = [];

	readonly periods = [
		{ value: 'last_7_days', label: this.translateService.instant('durations.last_7_days') },
		{ value: 'last_14_days', label: this.translateService.instant('durations.last_14_days') },
		{ value: 'last_30_days', label: this.translateService.instant('durations.last_30_days') },
		{ value: 'last_90_days', label: this.translateService.instant('durations.last_90_days') },
		{ value: 'year_to_date', label: this.translateService.instant('durations.year_to_date') },
		{ value: 'last_year', label: this.translateService.instant('durations.last_year') },
		{ value: 'since_installation', label: this.translateService.instant('durations.all_years') },
	];
	truckTypesOptions: any[] = [];
	manufacturersOptions: any[] = [];
	vintageOptions = this.siteDashboardService.vintageOptionsDropdown;
	
	assetUtilization: Array<{ label: string; value: number, showPercentage: boolean, percentage: number }> = [];
	performance: Array<{ label: string; value: number, showPercentage: boolean, percentage: number }> = [];

	lowTruckUtilization: Record<string, any> = {};
	lowTruckEnergyUsage: Record<string, any> = {};
	trucksOutsideGeofence: Record<string, any> = {};
	trucksWithDeepDischarge: Record<string, any> = {};
	trucksWithPotentialWeakBatteryCells: Record<string, any> = {};

	readonly staticColumnConfig = [
		{ key: 'truck_id', name: this.translateService.instant('devices.truck_id'), type: "link" },
		{ key: 'truck_sn', name: this.translateService.instant('devices.truck_sn'), type: "string" },
		{ key: 'truck_manufacturer', name: this.translateService.instant('devices.truck_manufacturer'), type: "string" },
		{ key: 'truck_type', name: this.translateService.instant('devices.truck_type'), type: "string" },
	];

	widgetsConfig: Record<string, any> = {
		low_truck_utilization: {
			title: this.translation.instant('exception_reports.low_truck_utilization'),
			tooltip: 'truck_utilization_report',
			name: 'low_truck_utilization',
			has_period_filter: true,
			has_voltage_filter: false,
			has_manufacturer_filter: true,
			has_vintage_filter: true,
			has_truck_type_filter: true,
			extra_fields: ['truck_utilization_score'],
			staticColumnConfig: this.staticColumnConfig,
		},
		low_truck_energy_usage: {
			title: this.translation.instant('exception_reports.low_truck_energy_usage'),
			tooltip: 'truck_energy_utilization_report',
			name: 'low_truck_energy_usage',
			has_period_filter: true,
			has_voltage_filter: false,
			has_manufacturer_filter: true,
			has_vintage_filter: true,
			has_truck_type_filter: true,
			extra_fields: ['truck_energy_usage_score'],
			staticColumnConfig: this.staticColumnConfig,
		},
		trucks_outside_geofence: {
			title: this.translation.instant('exception_reports.trucks_outside_geofence'),
			tooltip: 'truck_outside_geofence_report',
			name: 'trucks_outside_geofence',
			has_manufacturer_filter: true,
			has_vintage_filter: true,
			has_truck_type_filter: true,
			extra_fields: ['last_truck_location_long', 'last_truck_location_lat', 'days_outside_geofence'],
			staticColumnConfig: this.staticColumnConfig,
		},
		trucks_with_deep_discharge: {
			title: this.translation.instant('exception_reports.trucks_with_deep_discharge'),
			tooltip: 'trucks_with_deep_discharge_tooltip',
			name: 'trucks_with_deep_discharge',
			has_period_filter: true,
			has_voltage_filter: false,
			has_manufacturer_filter: true,
			has_vintage_filter: true,
			has_truck_type_filter: true,
			extra_fields: ['deep_discharge_cycles'],
			staticColumnConfig: this.staticColumnConfig,
		},
		trucks_with_potential_weak_battery_cells: {
			title: this.translation.instant('exception_reports.trucks_with_potential_weak_battery_cells'),
			tooltip: 'trucks_with_potential_weak_battery_cells_tooltip',
			name: 'trucks_with_potential_weak_battery_cells',
			has_period_filter: true,
			has_voltage_filter: false,
			has_manufacturer_filter: true,
			has_vintage_filter: true,
			has_truck_type_filter: true,
			extra_fields: ['low_discharge_cycles'],
			staticColumnConfig: this.staticColumnConfig,
		},
	};

	widgetsDataMapper: Record<string, string> = {
		low_truck_utilization: 'lowTruckUtilization',
		low_truck_energy_usage: 'lowTruckEnergyUsage',
		trucks_with_deep_discharge: 'trucksWithDeepDischarge',
		trucks_with_potential_weak_battery_cells: 'trucksWithPotentialWeakBatteryCells',
		trucks_outside_geofence: 'trucksOutsideGeofence',
	};

	PERCENT_WIDGETS = new Set([
		'low_truck_utilization',
		'low_truck_energy_usage',
	]);

	filters: {
		selectedPeriod: string;
		selectedTruckType: string;
		selectedVintage: string[];
		selectedManufacturer: string;
	} = {
			selectedPeriod: this.periods[0].value,
			selectedTruckType: 'all',
			selectedVintage: [this.siteDashboardService.vintageOptionsDropdown[0].value],
			selectedManufacturer: 'all',
		};

	metricsLegends: LegendGroup[] = [
		{
			title: this.translateService.instant("utilization_metrics.utilization_exceptions"),
			items: [
				{ label: 'Low (<3%)', severity: 'Low' },
				{ label: 'Moderate (3–5%)', severity: 'Moderate' },
				{ label: 'High (5–10%)', severity: 'High' },
				{ label: 'Critical (>10%)', severity: 'Critical' }
			],
		},
		{
			title: this.translateService.instant("utilization_metrics.discharge_cycle_severity"),
			items: [
				{ label: 'Low (<10%)', severity: 'Low' },
				{ label: 'Moderate (10–15%)', severity: 'Moderate' },
				{ label: 'High (15–25%)', severity: 'High' },
				{ label: 'Critical (>25%)', severity: 'Critical' }
			],
		}
	];

	constructor (
		private translation: TranslateService,
		private siteService: SitesService,
		private route: ActivatedRoute,
		private sideMenuService: SideMenuService,
		public commonService: CommonService,
		private translateService: TranslateService,
		private excelService: ExcelService,
		public siteDashboardService: SiteDashboardService,
	) {}

	ngOnInit() {
		const siteId = this.route?.parent?.snapshot?.params?.siteId || 0;
		if (!siteId)
			return;

		this.siteSub = this.sideMenuService.currentSite.subscribe((site: any) => {
			if (site) {
				this.commonService.setPageTabTitle(`${site['customer_name']} - ${site['name']} - ${this.translation.instant('exception_reports.charger_exception_reports')}`)
				this.siteInfo = site;

				this.getTruckExceptionReports();
			}
		});
	}

	getTruckExceptionReports() {
		this.siteService.getTruckExceptionReports(this.siteInfo.id).subscribe((response: any) => {
			this.devices = response.devices || [];

			this.lowTruckUtilization = response.low_truck_utilization || [];
			this.lowTruckEnergyUsage = response.low_truck_energy_usage || [];
			this.trucksOutsideGeofence = response.trucks_outside_geofence || [];
			this.trucksWithDeepDischarge = response.trucks_with_deep_discharge || [];
			this.trucksWithPotentialWeakBatteryCells = response.trucks_with_potential_weak_battery_cells || [];

			this.prepareOptions();
			this.formatUtilizationData();
		});
	}

	prepareOptions() {
		const truckTypes: string[] = [];
		const manufacturers: string[] = [];

		for (const device of this.devices) {
			if (!truckTypes.includes(device.truck_type))
				truckTypes.push(device.truck_type);

			if (!manufacturers.includes(device.truck_manufacturer))
				manufacturers.push(device.truck_manufacturer);
		}

		this.truckTypesOptions = [{ value: 'all', label: this.translateService.instant('g.all') }, ...truckTypes.map(value => ({ value, label: value }))];
		this.manufacturersOptions = [{ value: 'all', label: this.translateService.instant('g.all') }, ...manufacturers.map(value => ({ value, label: value }))];
	}

	formatUtilizationData() {
		this.filters = structuredClone(this.filters);

		const assetUtilizationData = [
			'low_truck_utilization',
			'low_truck_energy_usage',
			'trucks_outside_geofence',
		]

		const assetUtilization = [];

		for (const type of assetUtilizationData) {
			const value = this.calculateScore(type);

			assetUtilization.push({
				label: this.translation.instant(`utilization_metrics.${type}`),
				value: value,
				percentage: value,
				showPercentage: true,
				name: type,
			});
		}

		this.assetUtilization = assetUtilization;
		this.calculateBatteryPerformanceMetrics();
	}

	calculateBatteryPerformanceMetrics() {
		this.filters = structuredClone(this.filters);

		const performanceUtilizationData = [
			'trucks_with_deep_discharge',
			'trucks_with_potential_weak_battery_cells',
		];

		const performanceUtilization = [];

		for (const type of performanceUtilizationData) {
			const widgetDataKey = this.widgetsDataMapper[type];
			let selectedData = (this as any)[widgetDataKey]?.[this.filters.selectedPeriod];

			let reportEventsCount = 0;
			let totalEventsCount = 0;

			for (const macAddress in selectedData) {
				if (!this.isIncludedDevice(macAddress) || !selectedData[macAddress]?.showDevice)
					continue;

				reportEventsCount += selectedData[macAddress]?.value || 0;
				totalEventsCount += selectedData[macAddress]?.total_events || 0;
			}

			const value = totalEventsCount ? Math.round((reportEventsCount / totalEventsCount) * 100) : 0;

			performanceUtilization.push({
				label: this.translation.instant(`utilization_metrics.${type}`),
				value: value,
				percentage: value,
				displayValue: `${reportEventsCount} of ${totalEventsCount}`,
				showPercentage: true,
				name: type,
			});
		}

		this.performance = performanceUtilization;
	}

	calculateScore(chartType: string): number {
		const siteDevices = keyBy(this.devices, 'mac_address');

		const widgetDataKey = this.widgetsDataMapper[chartType];
		let selectedData = (this as any)[widgetDataKey]?.[this.filters.selectedPeriod];

		if (chartType == 'trucks_outside_geofence')
			selectedData = siteDevices;

		const totalDevicesCount = this.devices.filter(device => device.mac_address in selectedData && this.isIncludedDevice(device.mac_address)).length;

		let totalReportDevices = 0;
		for (const macAddress in selectedData) {
			if (chartType == 'trucks_outside_geofence' && siteDevices[macAddress].days_outside_geofence)
				totalReportDevices++;
			else if (this.isIncludedDevice(macAddress) && selectedData[macAddress]?.showDevice)
				totalReportDevices++;
		}

		return totalDevicesCount ? Math.round((totalReportDevices / totalDevicesCount) * 100) : 0;
	}

	isIncludedDevice(macAddress: string) {
		const siteDevices = keyBy(this.devices, 'mac_address');

		if (!this.siteDashboardService.isVintageWithinFilter(siteDevices?.[macAddress], this.filters.selectedVintage))
			return false;

		if (this.filters.selectedManufacturer !== 'all' && siteDevices?.[macAddress]?.truck_manufacturer !== this.filters.selectedManufacturer)
			return false;

		if (this.filters.selectedTruckType !== 'all' && siteDevices?.[macAddress]?.truck_type !== this.filters.selectedTruckType)
			return false;

		return true;
	}

	vintageSelectionChanged() {
		if (!this.filters.selectedVintage.length)
			this.filters.selectedVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];

		if (this.filters.selectedVintage.length > 1 && this.filters.selectedVintage.includes(this.siteDashboardService.vintageOptionsDropdown[0].value)) {
			if (this.filters.selectedVintage[0] == this.siteDashboardService.vintageOptionsDropdown[0].value)
				this.filters.selectedVintage = this.filters.selectedVintage.filter(value => value !== this.siteDashboardService.vintageOptionsDropdown[0].value);
			else
				this.filters.selectedVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];
		}

		this.formatUtilizationData();
	}
	
	exportReports() {
		const data = this.prepareDataForExcelExport();
		this.excelService.exportExcel(data, `Exception Report.xlsx`);
	}

	prepareDataForExcelExport(): ExcelSheet[] {
		const devices = keyBy(this.devices, 'mac_address');

		const csvHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.siteInfo?.customer_name,
			siteName: this.siteInfo?.name,
			includeSpacing: false
		}) || [];

		const headerInfoBlock: any[][] = [
			...csvHeaderInfo,
			[this.translateService.instant('exception_reports.time_period'), this.getSelectedPeriodLabel()],
			[this.translateService.instant('exception_reports.selected_filter_values')],
			...this.getSelectedFilterValuesRows(),
			[],
		];

		const sheetData: any[][] = [...headerInfoBlock];

		const headerInfoRows: number[] = [];
		headerInfoBlock.forEach((row, idx) => {
			if (row && row.length) headerInfoRows.push(idx);
		});

		const titleRows: number[] = [];
		const tables: any[] = [];

		let counter = 0
		for (const widgetKey in this.widgetsDataMapper) {
			const widgetDataKey = this.widgetsDataMapper[widgetKey];
			const widgetConfig = this.widgetsConfig[widgetKey];
			const widgetData = (this as any)[widgetDataKey]?.[this.filters.selectedPeriod];

			const staticFields = this.staticColumnConfig.map((field: any) => field.name);

			const header = [
				...staticFields,
				...widgetConfig.extra_fields.map((field: string) => this.translateService.instant(`exception_reports.${field}`))
			];

			if(counter) //ignore extra empty row in the first
				sheetData.push([]);

			const titleRowIndex = sheetData.length;
			sheetData.push([widgetConfig.title]);
			titleRows.push(titleRowIndex);

			const headerRowIndex = sheetData.length;
			sheetData.push(header);

			const dataStartRow = sheetData.length;

			const percentColumns = this.getPercentColumnIndexes(widgetKey, staticFields.length);

			const widgetDevicesData = widgetData || devices;

			for (const macAddress in widgetDevicesData) {
				const device = devices[macAddress] || {};

				if (!this.isIncludedDevice(macAddress) || (!widgetData?.[macAddress]?.showDevice && widgetKey != 'trucks_outside_geofence'))
					continue;

				const recordRow: any[] = this.staticColumnConfig.map((field: any) => device?.[field.key] || '');

				if (widgetData && widgetKey != 'trucks_outside_geofence') {
					recordRow.push(this.normalizePercentValue(widgetKey, widgetData[macAddress].value));
				} else {
					for (const field of widgetConfig.extra_fields) {
						recordRow.push(device[field]);
					}
				}

				sheetData.push(recordRow);
			}

			const dataEndRow = sheetData.length - 1;

			if (dataEndRow >= dataStartRow) {
				tables.push({
					headerRow: headerRowIndex,
					dataStartRow,
					dataEndRow,
					startCol: 0,
					endCol: header.length - 1,
					percentColumns,
				});
			} else {
				// No data: header stays bold, add a "No data" row
				const noDataRowIndex = sheetData.length;
				const noDataRow = new Array(header.length).fill('');
				noDataRow[0] = this.translateService.instant('g.no_data_to_show');
				sheetData.push(noDataRow);

				tables.push({
					headerRow: headerRowIndex,
					dataStartRow: noDataRowIndex,
					dataEndRow: noDataRowIndex,
					startCol: 0,
					endCol: header.length - 1,
					percentColumns: [],
					noData: true,
				});
			}

			counter++;
		}

		return [{
			data: sheetData,
			sheetName: this.translateService.instant('exception_reports.truck_exception_reports'),
			style: {
				headerInfoRows,
				titleRows,
				tables,
			},
		}];
	}

	getSelectedPeriodLabel(): string {
		return this.periods.find(p => p.value === this.filters.selectedPeriod)?.label || this.filters.selectedPeriod;
	}

	getSelectedFilterValuesRows(): any[][] {
		const allLabel = this.translateService.instant('g.all');

		const truckType = this.filters.selectedTruckType === 'all'
			? allLabel : this.filters.selectedTruckType;
		const manufacturer = this.filters.selectedManufacturer === 'all'
			? allLabel : this.filters.selectedManufacturer;

		const vintage = (this.filters.selectedVintage || [])
			.map(v => this.vintageOptions.find(o => o.value === v)?.label || v)
			.join(', ') || allLabel;

		return [
			[this.translateService.instant('devices.truck_type'), truckType],
			[this.translateService.instant('devices.truck_manufacturer'), manufacturer],
			[this.translateService.instant('devices.truck_vintage'), vintage],
		];
	}

	getPercentColumnIndexes(widgetKey: string, staticFieldCount: number): number[] {
		if (!this.PERCENT_WIDGETS.has(widgetKey))
			return [];

		return [staticFieldCount];
	}

	normalizePercentValue(widgetKey: string, value: any): any {
		if (!this.PERCENT_WIDGETS.has(widgetKey) || value == null || isNaN(value))
			return value;

		const num = Number(value);
		return num / 100;
	}

	get isNoData() {
		return !this.assetUtilization.some(metric => metric.value) && !this.performance.some(metric => metric.value);
	}

	ngOnDestroy() {
		if (this.siteSub)
			this.siteSub.unsubscribe();
	}
}
